import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { ModalOpenContext, useModalExitChildren, useModalFadeRef } from './modal-fade-presence'

import { cn } from "@/lib/utils"

function Dialog({ children, open, defaultOpen = false, onOpenChange, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  const [localOpen, setLocalOpen] = React.useState(defaultOpen)
  return <ModalOpenContext.Provider value={open ?? localOpen}>
    <DialogPrimitive.Root {...props} open={open} defaultOpen={defaultOpen} onOpenChange={next => {
      setLocalOpen(next)
      onOpenChange?.(next)
    }}>{children}</DialogPrimitive.Root>
  </ModalOpenContext.Provider>
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, onClick, ...props }, ref) => {
  const fadeRef = useModalFadeRef(ref)
  return (
    <DialogPrimitive.Overlay
      ref={fadeRef}
      className={cn(
        "modal-fade fixed inset-0 z-50 bg-black/70",
        className
      )}
      {...props}
      onClick={event => { event.stopPropagation(); onClick?.(event) }}
    />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Fill the browser viewport for forms and detailed workspaces. */
  fullScreen?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, onClick, onSubmit, fullScreen = false, style, ...props }, ref) => {
  const fadeRef = useModalFadeRef(ref)
  const exitChildren = useModalExitChildren(children)
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={fadeRef}
        className={cn(
          "modal-fade fixed left-[calc(50%_-_var(--duo-tasks-margin,0px)/2)] top-[50%] z-50 grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg rounded-lg focus:outline-none",
          className
        )}
        {...props}
        style={fullScreen ? {
          ...style,
          left: 0, top: 0, transform: 'none',
          width: '100%', maxWidth: 'none', height: '100dvh', maxHeight: '100dvh',
          borderRadius: 0, borderWidth: 0,
        } : style}
        // Portals still bubble through React parents, including the modal opener.
        onClick={event => { event.stopPropagation(); onClick?.(event) }}
        onSubmit={event => { event.stopPropagation(); onSubmit?.(event) }}
      >
        {exitChildren}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
